import React, { useState, useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import noop from 'lib/noop'
import {
  AprraisedValue,
  Discount,
  SaleValueText,
  AsteriskExplain,
  Financing,
  OpenSimulator,
  Button,
  FinancialData,
  AuctionDate,
  LineTrace,
  FinishedPropertyModal,
  NoticeInformation,
  CountDown,
  Text,
  JudicialSaleCard,
} from 'components'
import { AuctionContainer } from 'containers'
import classNames from 'classnames'
import { AuctionModalProvider, useSimulatorForm } from 'store'
import {
  isCampaignProperty,
  hasActiveCampaign,
  activeCountDownPropertyToFirstCampaign,
  activeCountDownPropertyToSecondCampaign,
  campaignConfigurations,
  timerFirstCampaign,
  timerSecondCampaign,
  replaceCampaignToShow,
} from 'utils/checkCampaign'
import { formatDate } from './utils'
import styles from './CallToAction.module.scss'

const CallToAction = ({
  estateData,
  proposalAction,
  contactUsAction,
  simulatorAction,
  scheduleAction,
  preview,
}) => {
  const { setField } = useSimulatorForm()
  const [modal, setModal] = React.useState('')
  const [displayCountdownFirst, setDisplayCountdownFirst] = useState(false)
  const [displayCountdownSecond, setDisplayCountdownSecond] = useState(false)

  useLayoutEffect(() => {
    setTimeout(() => {
      setDisplayCountdownFirst(activeCountDownPropertyToFirstCampaign)
      setDisplayCountdownSecond(activeCountDownPropertyToSecondCampaign)
    }, 1500)
  }, [])

  if (!estateData) {
    return null
  }

  const {
    appraisedPrice,
    appraisedLabel,
    mainValue,
    mainValueLabel,
    callToActionConsiderations,
    showDiscount,
    hideValue,
    desagio,
    showVisit,
    showContactUs,
    callToActionLabel,
    juridical,
    saleCategory,
    fullContactButton,
    contactButtonLabel,
    formas_pagamento_card,
    showAuctionDate,
    isDirectInstallment,
    isConcurrenceOver,
    auctionInfo,
    callToActionUrl,
    showFinanceEstate,
    hideConditions,
    salesman,
    contactInfo,
    isAuction,
    isPublicCompetition,
    propertyId,
    isSacre,
    campaignInfo,
    tags,
  } = estateData

  const {
    values: { campaignTotalValue },
  } = campaignInfo

  const hasPromotion = Boolean(
    campaignTotalValue && isCampaignProperty(tags) && hasActiveCampaign()
  )

  const isJudicialSale = saleCategory === 'venda_judicial'

  const nameCampaignValue = () => {
    if (tags.includes(process.env.REACT_APP_CAMPAIGN)) {
      return replaceCampaignToShow(process.env.REACT_APP_CAMPAIGN)
    }
    if (tags.includes(process.env.REACT_APP_SECOND_CAMPAIGN)) {
      return replaceCampaignToShow(process.env.REACT_APP_SECOND_CAMPAIGN)
    }
    return 'Campanha'
  }

  const appraisedLowerThanMain = appraisedPrice < mainValue

  const CAIXA_KEY = 'f0bad832-8ec9-aba2-b72d-5909efb6e044'

  const handleActionButton = () => {
    if (callToActionLabel === 'Encerrado') {
      return setModal('FinishedModal')
    }
    if (callToActionUrl) {
      return setModal('AuctionModal')
    }

    return proposalAction()
  }

  const handleFinancingClick = () => {
    setField('balance_amount', mainValue)
    setField('property_id', propertyId)
    simulatorAction()
  }
  const callToActionButton = () => (
    <Button
      name="possibleFixedButton"
      className={styles.largeButton}
      type="filled"
      onClick={handleActionButton}
      fontSize="medium"
      disabled={
        ((callToActionLabel === 'Vendido' || (isAuction && !callToActionUrl)) &&
          callToActionLabel !== 'Encerrado') ||
        isConcurrenceOver
      }
    >
      {callToActionLabel}
    </Button>
  )
  const titleCountDown = hasActiveCampaign()
    ? `${nameCampaignValue()} acaba em:`
    : ''

  return (
    <>
      {Boolean(
        process.env.REACT_APP_CAMPAIGN &&
          tags.includes(process.env.REACT_APP_CAMPAIGN) &&
          displayCountdownFirst &&
          campaignConfigurations(process.env.REACT_APP_CAMPAIGN)
            .countDownOnProperty
      ) && (
        <span className={styles.campaignCountDown}>
          <CountDown
            title={titleCountDown}
            endDate={timerFirstCampaign()}
            campaign={
              new RegExp(`\\[${process.env.REACT_APP_CAMPAIGN}\\]`, 'i')
            }
          />
        </span>
      )}
      {Boolean(
        process.env.REACT_APP_SECOND_CAMPAIGN &&
          tags.includes(process.env.REACT_APP_SECOND_CAMPAIGN) &&
          displayCountdownSecond &&
          campaignConfigurations(process.env.REACT_APP_SECOND_CAMPAIGN)
            .countDownOnProperty
      ) && (
        <span className={styles.campaignCountDown}>
          <CountDown
            title={titleCountDown}
            endDate={timerSecondCampaign()}
            campaign={
              new RegExp(`\\[${process.env.REACT_APP_SECOND_CAMPAIGN}\\]`, 'i')
            }
          />
        </span>
      )}

      <div className={styles.card}>
        <AuctionModalProvider>
          <AuctionContainer
            opened={modal === 'AuctionModal'}
            closeModal={() => setModal('')}
            url={callToActionUrl}
          />
        </AuctionModalProvider>

        <FinishedPropertyModal
          opened={modal === 'FinishedModal'}
          closeModal={() => setModal('')}
          contactInfo={{ name: contactInfo.name, phone: contactInfo.phone }}
        />
        <div
          className={classNames(styles.valuesContainer, {
            [styles.activeCampaign]: hasPromotion,
          })}
        >
          {isJudicialSale && <JudicialSaleCard juridicalData={juridical} />}

          {mainValue !== appraisedPrice && !appraisedLowerThanMain && (
            <AprraisedValue
              value={appraisedPrice}
              type="row"
              label={appraisedLabel}
              strike={mainValue !== appraisedPrice}
              isCampaign={hasPromotion}
            />
          )}
          {showDiscount && <Discount percent={desagio} />}
          <SaleValueText
            value={hideValue ? null : mainValue}
            saleLabel={mainValueLabel}
            asterisk={!!callToActionConsiderations.length}
            isCampaign={hasPromotion}
          />
          {!!hasPromotion && (
            <SaleValueText
              saleLabel={`Valor ${nameCampaignValue()}`}
              value={campaignTotalValue}
              propertyCard
              asterisk
              className={styles.promotionPrice}
            />
          )}
        </div>
        {!!callToActionConsiderations.length && (
          <AsteriskExplain
            callToActionConsiderations={callToActionConsiderations}
          />
        )}

        {showAuctionDate && isPublicCompetition ? (
          <div className={styles.timer}>
            <div className={styles.timerLabel}>
              <Text fontSize="medium">Essa concorrência acaba em</Text>
            </div>
            <div className={styles.countdown}>
              <CountDown
                endDate={formatDate(auctionInfo[0].data, auctionInfo[0].hora)}
              />
            </div>
          </div>
        ) : (
          Object.values(auctionInfo).map(auction => (
            <AuctionDate
              label={auction.label}
              date={auction.data}
              hour={auction.hora}
            />
          ))
        )}

        <LineTrace />

        {salesman.id === CAIXA_KEY && <NoticeInformation />}

        {!hideConditions && (
          <FinancialData paymentsMethods={formas_pagamento_card} />
        )}

        {!preview && (
          <>
            {isDirectInstallment && isSacre && (
              <OpenSimulator onClick={handleFinancingClick} />
            )}

            {showFinanceEstate && <Financing />}

            <div
              className={classNames(
                {
                  [styles.buttonContainer]: showContactUs || showVisit,
                },
                {
                  [styles.singleButtonContainer]: !showContactUs && !showVisit,
                }
              )}
            >
              {!preview && callToActionButton()}
              {!preview && (
                <div className={styles.horizontalContainer}>
                  {showContactUs && (
                    <Button
                      name="possibleFixedButton"
                      className={
                        fullContactButton
                          ? styles.largeButton
                          : styles.halfButton
                      }
                      type="filled"
                      onClick={contactUsAction}
                      fontSize="medium"
                      backgroundColor="secondColor"
                    >
                      {contactButtonLabel}
                    </Button>
                  )}
                  {preview}
                  {showVisit && (
                    <Button
                      name="possibleFixedButton"
                      className={
                        !showContactUs ? styles.largeButton : styles.halfButton
                      }
                      type="filled"
                      onClick={scheduleAction}
                      fontSize="medium"
                      disabled={
                        callToActionLabel === 'Vendido' ||
                        callToActionLabel === 'Encerrado'
                      }
                      backgroundColor="secondColor"
                    >
                      Agendar visita
                    </Button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  )
}

CallToAction.propTypes = {
  estateData: PropTypes.shape({
    formas_pagamento_card: PropTypes.arrayOf(PropTypes.object),
    callToActionConsiderations: PropTypes.arrayOf(PropTypes.string),
    juridical: PropTypes.shape({}).isRequired,
    saleCategory: PropTypes.string.isRequired,
    appraisedPrice: PropTypes.number.isRequired,
    appraisedLabel: PropTypes.string.isRequired,
    mainValue: PropTypes.number.isRequired,
    desagio: PropTypes.number.isRequired,
    isConcurrenceOver: PropTypes.bool.isRequired,
    propertyId: PropTypes.string.isRequired,
    auctionInfo: PropTypes.arrayOf(PropTypes.string),
    isSacre: PropTypes.bool.isRequired,

    mainValueLabel: PropTypes.string.isRequired,
    callToActionLabel: PropTypes.string.isRequired,
    contactButtonLabel: PropTypes.string.isRequired,
    callToActionUrl: PropTypes.string,
    auctionLabel: PropTypes.string,
    auctionDate: PropTypes.string,
    auctionHour: PropTypes.string,
    salesman: PropTypes.objectOf(PropTypes.string),

    showDiscount: PropTypes.bool.isRequired,
    hideValue: PropTypes.bool.isRequired,
    showProposal: PropTypes.bool.isRequired,
    showVisit: PropTypes.bool.isRequired,
    showContactUs: PropTypes.bool.isRequired,
    fullContactButton: PropTypes.bool.isRequired,
    showAuctionDate: PropTypes.bool.isRequired,
    showFinanceEstate: PropTypes.bool.isRequired,
    isDirectInstallment: PropTypes.bool.isRequired,
    hideConditions: PropTypes.bool.isRequired,
    contactInfo: PropTypes.shape({
      name: PropTypes.string,
      phone: PropTypes.string,
    }).isRequired,
    isAuction: PropTypes.bool.isRequired,
    isPublicCompetition: PropTypes.bool.isRequired,
    tags: PropTypes.arrayOf(PropTypes.string).isRequired,
    campaignInfo: PropTypes.shape({
      values: PropTypes.shape({
        campaignTotalValue: PropTypes.number.isRequired,
      }),
    }).isRequired,
  }).isRequired,
  proposalAction: PropTypes.func,
  contactUsAction: PropTypes.func,
  scheduleAction: PropTypes.func,
  simulatorAction: PropTypes.func,
  preview: PropTypes.bool,
}

CallToAction.defaultProps = {
  proposalAction: noop,
  contactUsAction: noop,
  scheduleAction: noop,
  simulatorAction: noop,
  preview: false,
}

export default React.memo(CallToAction)
